import React, { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Tabs from "aldoo-ra/Tabs"
import Tooltip from "aldoo-ra/Tooltip"
import Avatar from "aldoo-ra/Avatar"
import DataTable from "aldoo-ra/DataTable"
import { APIKeysPlatformAPI } from "../api-keys-api"
import { UserPlatformAPI } from "../user-api"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import PermissionsEditor from "./PermissionsEditor"
import {
  InformationCircleIcon,
  ShieldCheckIcon,
  ClockIcon,
  EyeIcon,
  QuestionMarkCircleIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"

// Rate limit presets - Updated to use recovery time system
const RATE_LIMIT_PRESETS = {
  'Development/Testing': {
    maxRequests: { sec: 10, min: 100, hour: 1000, day: 10000 },
    recoveryTimeSeconds: { sec: 5, min: 30, hour: 300, day: 1800 } // 5s, 30s, 5min, 30min
  },
  'Light Production': {
    maxRequests: { sec: 5, min: 50, hour: 500, day: 2000 },
    recoveryTimeSeconds: { sec: 10, min: 120, hour: 1800, day: 14400 } // 10s, 2min, 30min, 4h
  },
  'Standard Production': {
    maxRequests: { sec: 20, min: 200, hour: 2000, day: 20000 },
    recoveryTimeSeconds: { sec: 15, min: 300, hour: 3600, day: 28800 } // 15s, 5min, 1h, 8h
  },
  'High Volume': {
    maxRequests: { sec: 100, min: 2000, hour: 50000, day: 500000 },
    recoveryTimeSeconds: { sec: 30, min: 600, hour: 7200, day: 86400 } // 30s, 10min, 2h, 1d
  },
  'Enterprise': {
    maxRequests: { sec: 500, min: 10000, hour: 200000, day: 2000000 },
    recoveryTimeSeconds: { sec: 60, min: 1800, hour: 14400, day: 172800 } // 1min, 30min, 4h, 2d
  }
}

/**
 * Unified API Key Form Component
 * Handles both Create and Edit modes
 */
const APIKeyForm = ({
  mode = "create", // "create" or "edit"
  apiKey = null, // Required for edit mode
  onSuccess,
  onCancel,
  onDelete // Function to handle delete
}) => {
  const [formData, setFormData] = useState({
    description: '',
    validUntil: '',
    ipWhitelist: '',
    useCustomRateLimit: true,
    rateLimitPreset: 'Standard Production'
  })
  const [expirationType, setExpirationType] = useState('lifetime') // 'expiration' or 'lifetime'
  const [customRateLimit, setCustomRateLimit] = useState({
    maxRequests: { sec: "20", min: "100", hour: "1000", day: "10000" },
    recoveryTimeSeconds: { sec: "10", min: "300", hour: "7200", day: "86400" }
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [activeTab, setActiveTab] = useState("basic")
  const [currentStatus, setCurrentStatus] = useState('active')
  
  // User selection state (only for create mode)
  const [selectedUser, setSelectedUser] = useState(null)
  
  // Permissions editor state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  
  // User permissions state for display purposes
  const [currentUserPermissions, setCurrentUserPermissions] = useState([])

  // Find matching rate limit preset
  const findMatchingPreset = (rateLimit) => {
    if (!rateLimit) return null
    
    for (const [presetName, presetConfig] of Object.entries(RATE_LIMIT_PRESETS)) {
      if (JSON.stringify(presetConfig.maxRequests) === JSON.stringify(rateLimit.maxRequests) &&
          JSON.stringify(presetConfig.recoveryTimeSeconds) === JSON.stringify(rateLimit.recoveryTimeSeconds)) {
        return presetName
      }
    }
    return null
  }

  // Initialize form data based on mode
  useEffect(() => {
    if (mode === "edit" && apiKey) {
      // Edit mode: populate from existing API key
      setFormData({
        description: apiKey.description || '',
        validUntil: apiKey.validUntil ? 
          new Date(apiKey.validUntil).toISOString().slice(0, 16) : '',
        ipWhitelist: Array.isArray(apiKey.ipWhitelist) ? 
          apiKey.ipWhitelist.join(', ') : '',
        useCustomRateLimit: !!apiKey.rateLimit,
        rateLimitPreset: findMatchingPreset(apiKey.rateLimit) || 'Standard Production'
      })
      
      // Set expiration type based on whether API key has expiration
      setExpirationType(apiKey.validUntil ? 'expiration' : 'lifetime')

      if (apiKey?.rateLimit?.maxRequests && apiKey?.rateLimit?.recoveryTimeSeconds) {
        try {
          // Convert numeric values to strings for display
          const stringRateLimit = {
            maxRequests: {},
            recoveryTimeSeconds: {}
          }
          const units = ['sec', 'min', 'hour', 'day']
          units.forEach(unit => {
            if (apiKey.rateLimit.maxRequests && apiKey.rateLimit.recoveryTimeSeconds) {
              stringRateLimit.maxRequests[unit] = String(apiKey.rateLimit.maxRequests[unit] || 0)
              stringRateLimit.recoveryTimeSeconds[unit] = String(apiKey.rateLimit.recoveryTimeSeconds[unit] || 0)
            }
          })
          setCustomRateLimit(stringRateLimit)
        } catch (error) {
          console.error('Error processing rate limit data:', error)
        }
      }

      // In edit mode, set the API key owner as selectedUser for permissions
      if (apiKey.user) {
        setSelectedUser(apiKey.user)
      }
      
      // Set current status
      setCurrentStatus(apiKey.status || 'active')
    } else {
      // Create mode: use defaults
      setFormData({
        description: '',
        validUntil: '',
        ipWhitelist: '',
        useCustomRateLimit: true,
        rateLimitPreset: 'Standard Production'
      })
      
      // Reset expiration type to default (lifetime)
      setExpirationType('lifetime')
      // Initialize with default preset values instead of hardcoded values
      const defaultPreset = RATE_LIMIT_PRESETS['Standard Production']
      
      if (defaultPreset && defaultPreset.maxRequests && defaultPreset.recoveryTimeSeconds) {
        const stringRateLimit = {
          maxRequests: {},
          recoveryTimeSeconds: {}
        }
        const units = ['sec', 'min', 'hour', 'day']
        units.forEach(unit => {
          stringRateLimit.maxRequests[unit] = String(defaultPreset.maxRequests[unit] || 0)
          stringRateLimit.recoveryTimeSeconds[unit] = String(defaultPreset.recoveryTimeSeconds[unit] || 1)
        })
        setCustomRateLimit(stringRateLimit)
      } else {
        // Fallback to basic values if preset is not available
        setCustomRateLimit({
          maxRequests: { sec: "20", min: "200", hour: "2000", day: "20000" },
          recoveryTimeSeconds: { sec: "15", min: "300", hour: "3600", day: "28800" }
        })
      }
      setSelectedUser(null)
    }
  }, [mode, apiKey])


  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  // Handle permissions modal opening
  const handleOpenPermissionsModal = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!selectedUser) {
      showNotification({
        message: "Please select a user first",
        className: "bg-admin_error text-white",
        duration: 3000
      })
      return
    }
    setShowPermissionsModal(true)
  }

  // Handle rate limit changes
  const handleRateLimitChange = (section, field, value) => {
    // Store the raw value for display, validate on form submit
    setCustomRateLimit(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }))
  }

  // Handle preset change
  const handlePresetChange = (preset) => {
    console.log('handlePresetChange called with:', preset, 'Available presets:', Object.keys(RATE_LIMIT_PRESETS))
    
    if (!preset) {
      console.log('No preset provided, returning early')
      return
    }
    
    handleChange('rateLimitPreset', preset)
    
    const presetData = RATE_LIMIT_PRESETS[preset]
    if (!presetData) {
      console.log('Preset not found in RATE_LIMIT_PRESETS:', preset)
      return
    }
    
    if (!presetData.maxRequests || !presetData.recoveryTimeSeconds) {
      console.log('Preset data invalid:', presetData)
      return
    }
    
    try {
      const stringRateLimit = {
        maxRequests: {},
        recoveryTimeSeconds: {}
      }
      
      const units = ['sec', 'min', 'hour', 'day']
      units.forEach(unit => {
        stringRateLimit.maxRequests[unit] = String(presetData.maxRequests[unit] || 0)
        stringRateLimit.recoveryTimeSeconds[unit] = String(presetData.recoveryTimeSeconds[unit] || 1)
      })
      
      setCustomRateLimit(stringRateLimit)
    } catch (error) {
      console.error('Error in handlePresetChange:', error)
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    // User selection validation (only for create mode)
    if (mode === "create" && !selectedUser) {
      newErrors.user = "User selection is required"
    }

    if (!formData.description?.trim()) {
      newErrors.description = "Description is required"
    } else if (formData.description.trim().length > 500) {
      newErrors.description = "Description must be less than 500 characters"
    }

    // Permissions are determined by the user's role, no need to validate them separately

    // Validate expiration date (only when expiration type is 'expiration')
    if (expirationType === 'expiration' && formData.validUntil) {
      const expDate = new Date(formData.validUntil)
      if (expDate <= new Date()) {
        newErrors.validUntil = "Expiration date must be in the future"
      }
    } else if (expirationType === 'expiration' && !formData.validUntil) {
      newErrors.validUntil = "Expiration date is required when expiration type is selected"
    }

    // Validate IP whitelist
    if (formData.ipWhitelist?.trim()) {
      const ips = formData.ipWhitelist.split(',').map(ip => ip.trim())
      for (const ip of ips) {
        if (ip && !isValidIPv4(ip)) {
          newErrors.ipWhitelist = `Invalid IP address: ${ip}`
          break
        }
      }
    }

    // Validate custom rate limits
    const { maxRequests, recoveryTimeSeconds } = customRateLimit
    
    // Validate max requests (convert to numbers)
    const maxReqValues = ['sec', 'min', 'hour', 'day'].map(unit => {
      const val = parseFloat(String(maxRequests?.[unit] || '0').replace(',', '.')) || 0
      return val
    })
    if (maxReqValues.some(val => val <= 0)) {
      newErrors.rateLimit = "All rate limit values must be positive numbers"
    }
    
    // Validate recovery times (convert to numbers)
    const recoveryValues = ['sec', 'min', 'hour', 'day'].map(unit => {
      const val = parseFloat(String(recoveryTimeSeconds?.[unit] || '0').replace(',', '.')) || 0
      return val
    })
    if (recoveryValues.some(val => val <= 0)) {
      newErrors.recoveryTime = "All recovery times must be greater than 0 seconds"
    }
    // Check for reasonable ranges (1 second to 1 week)
    const maxRecoveryTime = 7 * 24 * 60 * 60 // 1 week in seconds
    if (recoveryValues.some(val => val > maxRecoveryTime)) {
      newErrors.recoveryTime = "Recovery times cannot exceed 1 week (604800 seconds)"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Simple IPv4 validation
  const isValidIPv4 = (ip) => {
    const ipRegex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/
    const match = ip.match(ipRegex)
    if (!match) return false
    
    return match.slice(1).every(octet => {
      const num = parseInt(octet, 10)
      return num >= 0 && num <= 255
    })
  }

  // Handle copy key to clipboard
  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key)
      showNotification({
        message: "API key copied to clipboard",
        className: "bg-admin_success text-white",
        duration: 2000
      })
    } catch (error) {
      showNotification({
        message: "Failed to copy API key",
        className: "bg-admin_error text-white",
        duration: 2000
      })
    }
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault()
    }
    
    if (!validateForm()) {
      // Show notification for validation errors and highlight problematic tab
      const errorFields = Object.keys(errors)
      if (errorFields.length > 0) {
        // Determine which tab has errors
        const securityTabErrors = ['validUntil', 'ipWhitelist']
        const limitsTabErrors = ['rateLimit', 'recoveryTime']
        
        let errorTab = 'basic'
        if (errorFields.some(field => limitsTabErrors.includes(field))) {
          errorTab = 'limits'
        } else if (errorFields.some(field => securityTabErrors.includes(field))) {
          errorTab = 'security'
        }
        
        const tabNames = {
          basic: 'Basic Info',
          security: 'Security', 
          limits: 'Rate Limits'
        }
        
        showNotification({
          message: `Please fix validation errors in the ${tabNames[errorTab]} tab`,
          className: "bg-admin_error text-white",
          duration: 4000
        })
        
        // Switch to the tab with errors
        setActiveTab(errorTab)
      }
      return
    }

    try {
      setLoading(true)

      if (mode === "create") {
        // Create new API key
        const createData = {
          description: formData.description.trim(),
          targetUserId: selectedUser._id
        }

        // Add optional fields
        if (expirationType === 'expiration' && formData.validUntil) {
          createData.validUntil = new Date(formData.validUntil).toISOString()
        } else if (expirationType === 'lifetime') {
          // Explicitly set to null for lifetime keys
          createData.validUntil = null
        }

        if (formData.ipWhitelist?.trim()) {
          createData.ipWhitelist = formData.ipWhitelist
            .split(',')
            .map(ip => ip.trim())
            .filter(ip => ip.length > 0)
        }

        // Add rate limit settings
        // Convert string values to numbers for API
        const numericRateLimit = {
          maxRequests: {},
          recoveryTimeSeconds: {}
        }
        
        // Add defensive checks to ensure the objects exist
        if (customRateLimit && customRateLimit.maxRequests && customRateLimit.recoveryTimeSeconds) {
          ['sec', 'min', 'hour', 'day'].forEach(unit => {
            numericRateLimit.maxRequests[unit] = parseFloat(String(customRateLimit.maxRequests[unit] || '0').replace(',', '.')) || 0
            numericRateLimit.recoveryTimeSeconds[unit] = parseFloat(String(customRateLimit.recoveryTimeSeconds[unit] || '0').replace(',', '.')) || 0
          })
        } else {
          // Fallback to default values if customRateLimit is not properly initialized
          ['sec', 'min', 'hour', 'day'].forEach(unit => {
            numericRateLimit.maxRequests[unit] = 0
            numericRateLimit.recoveryTimeSeconds[unit] = 0
          })
        }
        
        createData.rateLimit = numericRateLimit

        const response = await APIKeysPlatformAPI.CreateAPIKey(createData)

        if (response.result) {
          // Show success message with the full key (only time it's shown)
          showNotification({
            message: (
              <div>
                <div>API Key created successfully!</div>
                <div className="mt-2 p-2 bg-black bg-opacity-20 rounded font-mono text-xs">
                  {response.key}
                </div>
                <div className="text-xs mt-1">⚠️ Save this key now - it won't be shown again!</div>
              </div>
            ),
            className: "bg-admin_success text-white",
            duration: 10000 // Longer duration for important info
          })
          
          onSuccess(response.apiKey)
        } else {
          showNotification({
            message: response.error || "Failed to create API key",
            className: "bg-admin_error text-white",
            duration: 4000
          })
        }
      } else {
        // Edit existing API key
        const updateData = {
          key: apiKey.key,
          description: formData.description.trim(),
          status: currentStatus
        }
        

        // Handle expiration date based on expiration type
        if (expirationType === 'expiration' && formData.validUntil) {
          updateData.validUntil = new Date(formData.validUntil).toISOString()
        } else {
          // For 'lifetime' type or empty expiration, set to null (never expires)
          updateData.validUntil = null
        }

        // Handle IP whitelist
        if (formData.ipWhitelist?.trim()) {
          updateData.ipWhitelist = formData.ipWhitelist
            .split(',')
            .map(ip => ip.trim())
            .filter(ip => ip.length > 0)
        } else {
          updateData.ipWhitelist = []
        }

        // Handle rate limits
        // Convert string values to numbers for API
        const numericRateLimit = {
          maxRequests: {},
          recoveryTimeSeconds: {}
        }
        
        // Add defensive checks to ensure the objects exist
        if (customRateLimit && customRateLimit.maxRequests && customRateLimit.recoveryTimeSeconds) {
          ['sec', 'min', 'hour', 'day'].forEach(unit => {
            numericRateLimit.maxRequests[unit] = parseFloat(String(customRateLimit.maxRequests[unit] || '0').replace(',', '.')) || 0
            numericRateLimit.recoveryTimeSeconds[unit] = parseFloat(String(customRateLimit.recoveryTimeSeconds[unit] || '0').replace(',', '.')) || 0
          })
        } else {
          // Fallback to default values if customRateLimit is not properly initialized
          ['sec', 'min', 'hour', 'day'].forEach(unit => {
            numericRateLimit.maxRequests[unit] = 0
            numericRateLimit.recoveryTimeSeconds[unit] = 0
          })
        }
        
        updateData.rateLimit = numericRateLimit

        const response = await APIKeysPlatformAPI.UpdateAPIKey(updateData)

        if (response.result) {
          onSuccess(response.apiKey)
        } else {
          showNotification({
            message: response.error || "Failed to update API key",
            className: "bg-admin_error text-white",
            duration: 4000
          })
        }
      }
    } catch (error) {
      showNotification({
        message: error.message || `Failed to ${mode} API key`,
        className: "bg-admin_error text-white",
        duration: 4000
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle API key deletion
  const handleDelete = async () => {
    if (!apiKey?._id) return

    try {
      const confirmed = await showPromptDialogAsync({
        title: "Delete API Key",
        message: `Are you sure you want to permanently delete the API key "${apiKey.description}"? This action cannot be undone and the API key will immediately stop working.`,
        confirmText: "Delete",
        cancelText: "Cancel"
      })

      if (!confirmed) return

      setLoading(true)
      const response = await APIKeysPlatformAPI.HardDeleteAPIKey(apiKey._id)

      if (response.result) {
        showNotification({
          message: `API key "${apiKey.description}" deleted successfully`,
          className: "bg-admin_success text-white",
          duration: 3000
        })
        onDelete && onDelete(apiKey)
      } else {
        showNotification({
          message: response.error || "Failed to delete API key",
          className: "bg-admin_error text-white",
          duration: 4000
        })
      }
    } catch (error) {
      showNotification({
        message: error.message || "Failed to delete API key",
        className: "bg-admin_error text-white",
        duration: 4000
      })
    } finally {
      setLoading(false)
    }
  }

  const rateLimitPresetOptions = Object.keys(RATE_LIMIT_PRESETS).map(preset => ({
    value: preset,
    name: preset
  }))

  const isCreateMode = mode === "create"
  const isEditMode = mode === "edit"

  // Helper function to format recovery time seconds into human-readable format
  const formatRecoveryTime = (seconds) => {
    const numSeconds = parseInt(seconds) || 0
    
    if (numSeconds === 0) return "0 seconds"
    if (numSeconds < 60) return `${numSeconds} second${numSeconds === 1 ? '' : 's'}`
    
    // For values 1 minute to 59 minutes
    if (numSeconds < 3600) {
      const minutes = Math.round(numSeconds / 60)
      return `${minutes} min${minutes === 1 ? '' : 's'}`
    }
    
    // For values 1 hour to 23 hours
    if (numSeconds < 86400) {
      const hours = numSeconds / 3600
      // If it's close to whole hours, show as hours
      if (hours % 1 === 0) {
        return `${Math.round(hours)} hour${Math.round(hours) === 1 ? '' : 's'}`
      }
      // If it has significant minutes, show hours and minutes
      const wholeHours = Math.floor(hours)
      const remainingMinutes = Math.round((hours - wholeHours) * 60)
      if (remainingMinutes === 0) {
        return `${wholeHours} hour${wholeHours === 1 ? '' : 's'}`
      } else if (wholeHours === 0) {
        return `${remainingMinutes} min${remainingMinutes === 1 ? '' : 's'}`
      } else {
        return `${wholeHours}h ${remainingMinutes}min`
      }
    }
    
    // For values 1 day and above
    const days = numSeconds / 86400
    // If it's close to whole days, show as days
    if (days % 1 === 0 || days < 7) {
      const wholeDays = Math.round(days)
      return `${wholeDays} day${wholeDays === 1 ? '' : 's'}`
    }
    
    // For very large values, show weeks
    if (days >= 7) {
      const weeks = Math.round(days / 7)
      if (weeks < 4) {
        return `${weeks} week${weeks === 1 ? '' : 's'}`
      } else {
        const months = Math.round(weeks / 4.33) // approximately 4.33 weeks per month
        return `${months} month${months === 1 ? '' : 's'}`
      }
    }
    
    return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`
  }


  // Determine which tabs have errors
  const basicTabErrors = ['user', 'description', 'permissions']
  const securityTabErrors = ['validUntil', 'ipWhitelist', 'rateLimit', 'recoveryTime']
  const hasBasicTabErrors = Object.keys(errors).some(field => basicTabErrors.includes(field))
  const hasSecurityTabErrors = Object.keys(errors).some(field => securityTabErrors.includes(field))

  // Rate limit table data - use useMemo to ensure it updates when customRateLimit changes
  const rateLimitData = React.useMemo(() => 
    ['sec', 'min', 'hour', 'day'].map(unit => ({
      id: unit,
      window: unit,
      maxRequests: customRateLimit.maxRequests[unit],
      recoveryTimeSeconds: customRateLimit.recoveryTimeSeconds?.[unit] || "0",
      formattedRecoveryTime: formatRecoveryTime(customRateLimit.recoveryTimeSeconds?.[unit] || "0")
    })), [customRateLimit])

  // Rate limit table renders
  const rateLimitRenders = [
    {
      id: 'window',
      title: 'Time Window',
      headerClassName: "w-[100px]",
      render: (value) => (
        <div className="font-medium text-sm">
          /{value}
        </div>
      )
    },
    {
      id: 'maxRequests',
      title: (
        <div className="flex items-center gap-2">
          <span>Max Requests</span>
          
          <Tooltip
            content={
              <div className="max-w-sm">
                <Typography 
                  variant="admin_label"
                  className="mt-0 mb-2"
                >
                  Request Limits per Time Window
                </Typography>

                <div className="space-y-1">
                  <Typography variant="admin_txt1" class="block">
                    <span class="font-semibold">sec:</span> Maximum requests allowed per second
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    <span class="font-semibold">min:</span> Maximum requests allowed per minute
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    <span class="font-semibold">hour:</span> Maximum requests allowed per hour
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    <span class="font-semibold">day:</span> Maximum requests allowed per day
                  </Typography>
                </div>

                <Typography variant="admin_txt1" class="block mt-3">
                  The rate limiter tracks requests across all time windows simultaneously. 
                  When any window limit is exceeded, the recovery time system is applied.
                </Typography>
              </div>
            }
            variant="admin"
            position="top"
          >
            <QuestionMarkCircleIcon 
              className="w-4 h-4 text-admin_neutral_3/50 hover:text-admin_neutral_3 transition-colors cursor-help" 
            />
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[150px]",
      render: (value, row) => (
        <Input
          type="number"
          value={value}
          onChange={(e) => handleRateLimitChange('maxRequests', row.window, e.target.value)}
          min="1"
          className="w-full text-xs"
        />
      )
    },
    {
      id: 'recoveryTimeSeconds',
      title: (
        <div className="flex items-center gap-2">
          <span>Recovery Time (sec)</span>

          <Tooltip
            content={
              <div className="max-w-sm">
                <Typography 
                  variant="admin_label"
                  className="mt-0 mb-2"
                >
                  Recovery Time System
                </Typography>

                <div className="space-y-1">
                  <Typography variant="admin_txt1" class="block mb-3">
                    When a rate limit is exceeded, the user must wait the specified recovery time before normal access is restored.
                  </Typography>

                  <Typography 
                    variant="admin_txt1_accent" 
                    className="block"
                  >
                    Examples:
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    • 10 seconds = Quick recovery for burst limits
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    • 300 seconds (5 min) = Moderate recovery time
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    • 7200 seconds (2 hours) = Strict recovery time
                  </Typography>
                </div>

                <div className="mt-2 space-y-1">
                  <Typography 
                    variant="admin_txt1_accent" 
                    className="block"
                  >
                    Time Ranges:
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    • Minimum: 1 second
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    • Maximum: 604,800 seconds (1 week)
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    • Recommended: 10s-3600s for most use cases
                  </Typography>
                </div>

                <div className="mt-2 border-t border-admin_bordered space-y-1 pt-2">
                  <Typography 
                    variant="admin_txt1_accent" 
                    className="block"
                  >
                    Recovery Process:
                  </Typography>

                  <Typography variant="admin_txt1" class="block">
                    Once the recovery time elapses after the last rate limit violation, normal request processing resumes automatically.
                  </Typography>
                  
                </div>
              </div>
            }
            variant="admin"
            position="top"
          >
            <QuestionMarkCircleIcon 
              className="w-4 h-4 text-admin_neutral_3/50 hover:text-admin_neutral_3 transition-colors cursor-help" 
            />
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[180px]",
      render: (value, row) => (
        <Input
          type="number"
          value={value}
          onChange={(e) => handleRateLimitChange('recoveryTimeSeconds', row.window, e.target.value)}
          min="1"
          max="604800"
          placeholder="60"
          className="w-full text-xs"
        />
      )
    },
    {
      id: 'formattedRecoveryTime',
      title: 'Human Readable',
      headerClassName: "w-[120px]",
      render: (value) => (
        <span className="text-xs text-admin_text">{value}</span>
      )
    }
  ]

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs
        variant="admin"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { 
            value: "basic", 
            label: (
              <div className={`flex items-center space-x-2 ${hasBasicTabErrors ? 'text-admin_error' : ''}`}>
                <InformationCircleIcon className="w-4 h-4" />
                <span>Basic Info</span>

                {hasBasicTabErrors && <span className="w-2 h-2 bg-admin_error rounded-full"></span>}
              </div>
            )
          },
          { 
            value: "security", 
            label: (
              <div className={`flex items-center space-x-2 ${hasSecurityTabErrors ? 'text-admin_error' : ''}`}>
                <ShieldCheckIcon className="w-4 h-4" />
                <span>Security</span>

                {hasSecurityTabErrors && <span className="w-2 h-2 bg-admin_error rounded-full"></span>}
              </div>
            )
          },
          { 
            value: "limits", 
            label: (
              <div className="flex items-center space-x-2">
                <ClockIcon className="w-4 h-4" />
                <span>Rate Limits</span>
              </div>
            )
          },
        ]}
        className="mb-6"
      />

      <div className="space-y-6">
        {/* Tab Content */}
        {activeTab === "basic" && (
          <div className="space-y-6">
            {/* Description - First field */}
            <div>
              <Typography 
                variant="admin_txt1_accent"
                className="block text-admin_text mb-1"
              >
                Description *
              </Typography>

              <Input
                type="text"
                variant="admin"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="e.g., API key for mobile app integration"
                error={errors.description}
                required
                className="text-sm"
              />

              <Typography 
                variant="admin_txt1_dim" 
                className="block mt-2"
              >
                A clear description of what this API key will be used for
              </Typography>
            </div>

            {/* API Key Info (Edit mode only) */}
            {isEditMode && apiKey && (
              <div className="bg-admin_canvas rounded-xl p-4">
                <Typography 
                  component="h2"
                  variant="admin_label" 
                  className="mb-3"
                >
                  API Key Information
                </Typography>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex flex-col space-y-1">
                    <Typography variant="admin_txt1_dim">
                      Key ID
                    </Typography>

                    <div className="relative">
                      <code className="flex w-full h-10 items-center font-mono text-sm text-admin_text bg-admin_paper p-2 pr-10 rounded-md border border-admin_bordered">
                        {apiKey.key?.substring(0, 8)}...{apiKey.key?.substring(apiKey.key.length - 4)}
                      </code>

                      <Button
                        variant="admin_icon"
                        onClick={() => handleCopyKey(apiKey.key)}
                        title="Copy full key to clipboard"
                        className="absolute right-1 top-1/2 text-admin_text_secondary transform -translate-y-1/2 p-1"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-1">
                    <Typography variant="admin_txt1_dim">
                      Status
                    </Typography>

                    <DropdownSelect
                      variant="admin"
                      value={currentStatus ? [{ value: currentStatus, name: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1) }] : []}
                      onChange={(selectedOptions) => {
                        if (selectedOptions && selectedOptions.length > 0) {
                          setCurrentStatus(selectedOptions[0].value)
                        }
                      }}
                      options={[
                        { value: 'active', name: 'Active' },
                        { value: 'disabled', name: 'Disabled' },
                        { value: 'revoked', name: 'Revoked' }
                      ]}
                      multiSelection={false}
                      className="w-full"
                    />
                  </div>
                  
                  <div className="flex flex-col space-y-1">
                    <Typography variant="admin_txt1_dim">
                      Created
                    </Typography>

                    <span className="text-admin_text text-sm bg-admin_paper rounded-md border border-admin_bordered p-2">
                      {apiKey.createdAt ? new Date(apiKey.createdAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  
                  {apiKey.user && (
                    <div className="flex flex-col space-y-1">
                      <Typography variant="admin_txt1_dim">
                        Owner
                      </Typography>

                      <span className="text-admin_text text-sm bg-admin_paper rounded-md border border-admin_bordered p-2">
                        {apiKey.user.name || apiKey.user.email || 'Unknown'}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col space-y-1">
                    <Typography variant="admin_txt1_dim">
                      Last Used
                    </Typography>

                    <span className="text-admin_text text-sm bg-admin_paper rounded-md border border-admin_bordered p-2">
                      {(() => {
                        if (!apiKey.lastUsedAt) return 'Never'
                        const lastUsed = new Date(apiKey.lastUsedAt)
                        const now = new Date()
                        const diffMs = now - lastUsed
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

                        if (diffDays === 0) {
                          const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
                          if (diffHours === 0) {
                            const diffMinutes = Math.floor(diffMs / (1000 * 60))
                            return `${diffMinutes}m ago`
                          }
                          return `${diffHours}h ago`
                        } else if (diffDays < 7) {
                          return `${diffDays}d ago`
                        } else {
                          return lastUsed.toLocaleDateString()
                        }
                      })()}
                    </span>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Typography variant="admin_txt1_dim">
                      Issued By
                    </Typography>

                    <span className="text-admin_text text-sm bg-admin_paper rounded-md border border-admin_bordered p-2">
                      {(() => {
                        if (apiKey.issuedBy && typeof apiKey.issuedBy === 'object' && apiKey.issuedBy.name) {
                          return `${apiKey.issuedBy.name}${apiKey.issuedBy.email ? ` (${apiKey.issuedBy.email})` : ''}`
                        } else if (typeof apiKey.issuedBy === 'string' && apiKey.issuedBy !== 'System') {
                          return apiKey.issuedBy
                        } else {
                          return 'System'
                        }
                      })()}
                    </span>
                  </div>

                  {apiKey.status === 'revoked' && apiKey.revokedAt && (
                    <div className="flex flex-col space-y-1">
                      <Typography variant="admin_txt1_dim">
                        Revoked
                      </Typography>

                      <span className="bg-admin_paper rounded border border-admin_bordered text-sm text-admin_error p-2">
                        {(() => {
                          const revokedDate = new Date(apiKey.revokedAt)
                          let revokedText = revokedDate.toLocaleDateString()

                          if (apiKey.revokedBy) {
                            if (typeof apiKey.revokedBy === 'object' && apiKey.revokedBy.name) {
                              revokedText += ` by ${apiKey.revokedBy.name}`
                            } else if (typeof apiKey.revokedBy === 'string') {
                              revokedText += ` by ${apiKey.revokedBy}`
                            }
                          }

                          return revokedText
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Permissions Section for Edit Mode */}
                  {selectedUser && (
                    <div className="col-span-1 md:col-span-2 pt-3">
                      <Button
                        variant="admin_secondary"
                        onClick={() => setShowPermissionsModal(true)}
                        className="w-full"
                      >
                        <EyeIcon className="w-4 h-4 mr-2" />

                        <span className="font-medium">
                          {currentUserPermissions.length > 0 
                            ? `View ${currentUserPermissions.length} permission${currentUserPermissions.length === 1 ? '' : 's'}`
                            : 'Permissions'
                          }
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* User Selection (Create mode only) */}
            {isCreateMode && (
              <div>
                <Typography 
                  variant="admin_txt1_accent"
                  className="block text-admin_text mb-1"
                >
                  API Key Owner *
                </Typography>

                <DropdownSelect
                  variant="admin"
                  searchEnabled
                  useExternalSearch
                  enableResetButton
                  showCheckbox={false}
                  className="w-full"
                  apiMethod={async (params) => {
                    if (!params.searchTerm || params.searchTerm.length < 2) {
                      return { data: [] }
                    }

                    const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
                    return response
                  }}
                  onDataLoaded={(rawData) => {
                    return rawData.map(user => ({
                      _id: user._id,
                      name: user.name || 'Unknown User',
                      email: user.email || 'No email',
                      value: user._id,
                      label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
                      ...user
                    }))
                  }}
                  searchField="name email"
                  valueField="_id"
                  nameField="label"
                  placeholder="Search and select user..."
                  noDataMessage="Type to search users"
                  value={selectedUser ? [selectedUser] : []}
                  onChange={(value) => {
                    const user = value && value.length > 0 ? value[0] : null
                    setSelectedUser(user)
                    
                    // Reset permissions when user is removed or changed
                    if (!user) {
                      setCurrentUserPermissions([])
                    }
                    
                    if (errors.user) {
                      setErrors(prev => ({ ...prev, user: null }))
                    }
                  }}
                  error={errors.user}
                  onValueItemRender={(user) => {
                    if (!user) return "Search and select user..."
                    return (
                      <div className="flex items-center gap-3">
                        <Avatar user={user} variant="admin_small" />

                        <div className="flex-1 min-w-0">
                          <Typography 
                            variant="admin_label"
                            className="leading-none truncate my-0"
                          >
                            {user.name || 'Unknown User'}
                          </Typography>

                          <Typography 
                            variant="admin_txt2_dim"
                            className="block truncate"
                          >
                            {user.email || 'No email'}
                          </Typography>
                        </div>
                      </div>
                    )
                  }}
                  onItemRender={(user) => (
                    <div className="flex items-center gap-3 p-2">
                      <Avatar user={user} variant="admin_dropdown" />

                      <div className="flex-1">
                        <Typography 
                          variant="admin_label" 
                          className="leading-none my-0"
                        >
                          {user.name || 'Unknown User'}
                        </Typography>

                        <Typography 
                          variant="admin_txt2_dim"
                          className="block truncate"
                        >
                          {user.email || 'No email'}
                        </Typography>
                      </div>
                    </div>
                  )}
                />
                <Typography 
                  variant="admin_txt1_dim" 
                  className="block mt-2"
                >
                  Select the user who will own this API key
                </Typography>
              </div>
            )}

            {/* Permissions (Create mode only) */}
            {isCreateMode && (
              <div>
                <Typography 
                  variant="admin_txt1_accent"
                  className="block text-admin_text mb-1"
                >
                  Permissions *
                </Typography>

                {selectedUser ? (
                  <div className="flex items-center justify-between">
                    <Typography variant="admin_txt1_dim">
                      {currentUserPermissions.length} permission{currentUserPermissions.length === 1 ? '' : 's'} available
                    </Typography>

                    <Button
                      type="button"
                      variant="admin_secondary"
                      onClick={handleOpenPermissionsModal}
                    >
                      <EyeIcon className="w-4 h-4 mr-2" />
                      View Permissions
                    </Button>
                  </div>
                ) : (
                  <Typography variant="admin_txt1_dim">
                    Please select a user first
                  </Typography>
                )}

                {errors.permissions && (
                  <Typography 
                    variant="admin_txt2" 
                    className="block text-admin_error mt-1"
                  >
                    {errors.permissions}
                  </Typography>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-admin_text mb-1">
                Expiration
              </label>

              <div className="space-y-2">
                <DropdownSelect
                  variant="admin"
                  value={[{ value: expirationType, name: expirationType === 'expiration' ? 'Expiration Date' : 'Lifetime' }]}
                  onChange={(selected) => {
                    const newType = selected[0]?.value
                    setExpirationType(newType)
                    // Clear the date when switching to lifetime
                    if (newType === 'lifetime') {
                      handleChange('validUntil', '')
                    }
                  }}
                  options={[
                    { value: 'expiration', name: 'Expiration Date' },
                    { value: 'lifetime', name: 'Lifetime' }
                  ]}
                  multiSelection={false}
                  className="w-full"
                />
                
                {expirationType === 'expiration' && (
                  <Input
                    type="datetime-local"
                    value={formData.validUntil}
                    onChange={(e) => handleChange('validUntil', e.target.value)}
                    error={errors.validUntil}
                  />
                )}
              </div>
              
              <Typography 
                variant="admin_txt1_dim" 
                className="block mt-2"
              >
                {expirationType === 'expiration' 
                  ? 'Set a specific date and time when this API key will expire'
                  : 'This API key will never expire automatically'
                }
              </Typography>
            </div>
          </div>
        )}

        {activeTab === "security" && (
          <div className="space-y-4">
            <div>
              <Typography 
                variant="admin_txt1_accent"
                className="block text-admin_text mb-1"
              >
                IP Whitelist (Optional)
              </Typography>

              <Input
                variant="admin"
                multiline={true}
                value={formData.ipWhitelist}
                onChange={(e) => handleChange('ipWhitelist', e.target.value)}
                placeholder="192.168.1.100, 10.0.0.50"
                error={errors.ipWhitelist}
                className="text-sm"
              />

              <Typography 
                variant="admin_txt1_dim" 
                className="mt-1"
              >
                Comma-separated list of IP addresses. Leave empty to allow any IP
              </Typography>
            </div>

            <div className="bg-admin_info_bg rounded-xl p-4 pt-8">
              <div className="flex items-center gap-2">
                <InformationCircleIcon 
                  className="w-5 h-5 text-admin_info flex-shrink-0" 
                />

                <Typography 
                  variant="admin_body" 
                  className="text-admin_info font-medium my-0"
                >
                  Security Best Practices
                </Typography>
              </div>

              <ul className="list-disc list-outside pl-7 text-sm text-admin_info">
                <li>Use IP whitelisting for production keys</li>
                <li>Set expiration dates for temporary access</li>
                <li>Grant minimal required permissions</li>
                <li>Rotate keys regularly</li>
              </ul>
              
            </div>
          </div>
        )}

        {activeTab === "limits" && (
          <div className="space-y-4">
            <div className="space-y-4 p-4 bg-admin_neutral rounded-2xl">
              <div>
                <Typography 
                  variant="admin_txt1_accent"
                  className="block text-admin_text mb-1"
                >
                  Rate Limit Preset
                </Typography>

                <DropdownSelect
                  variant="admin"
                  value={rateLimitPresetOptions.filter(option => option.value === formData.rateLimitPreset)}
                  onChange={(selected) => {
                    console.log('DropdownSelect onChange:', selected)
                    const value = selected && selected.length > 0 ? selected[0].value : null
                    handlePresetChange(value)
                  }}
                  options={rateLimitPresetOptions}
                  multiSelection={false}
                  valueField="value"
                  nameField="name"
                  className="w-full"
                />
              </div>

              <DataTable
                key={JSON.stringify(customRateLimit)}
                variant="admin_compact"
                data={rateLimitData}
                localUserPreferencesKey="admin.platformAccess.apiKeyForm"
                renders={rateLimitRenders}
                showSearch={false}
                showFooter={false}
                itemHeight="h-[50px]"
                preferredHeight="auto"
              />
              
              {(errors.rateLimit || errors.recoveryTime) && (
                <Typography 
                  variant="admin_txt1" 
                  className="text-admin_error"
                >
                  {errors.rateLimit || errors.recoveryTime}
                </Typography>
              )}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-admin_bordered">
          <div>
            {!isCreateMode && apiKey && onDelete && (
              <Button
                type="button"
                variant="admin_error"
                onClick={handleDelete}
                disabled={loading}
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete API Key
              </Button>
            )}
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="admin_secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              variant="admin_primary"
              loading={loading}
            >
              {isCreateMode ? "Create API Key" : "Update API Key"}
            </Button>
          </div>
        </div>
      </div>

      {/* Permissions Editor Modal */}
      <PermissionsEditor
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        user={selectedUser}
        readOnly={!isCreateMode}
        onPermissionChange={isCreateMode ? (updatedPermissions) => {
          // Update current user permissions to reflect the changes (create mode only)
          setCurrentUserPermissions(updatedPermissions)
        } : undefined}
        onPermissionsLoaded={(permissions) => {
          // Update permissions display when initially loaded
          setCurrentUserPermissions(permissions)
        }}
        loading={loading}
      />
    </div>
  )
}

export default APIKeyForm